import { stableHash } from '@srcDS/utils/stableHash';
import clsx from 'clsx';
import React from 'react';
import { useLayoutContext } from '../../../../provider/layout/LayoutContext';
import { TagList } from '../../../atoms/tag/TagList';
import ArticleHeading from '../../../molecules/journal/articleHeading';
import ArticleSection from '../../../molecules/journal/articleSection';
import JournalArticlesSlider from '../../../molecules/journal/journalArticlesSlider';
import JournalProductsSlider from '../../../molecules/journal/journalArticlesSlider/JournalProductsSlider';
import JournalBanner from '../../../molecules/journal/journalBanner';
import JournalNavigation from '../../../molecules/journal/journalNavigation';
import SocialShare from '../../../organisms/socialShare';
import { Text } from '../../../typography/text/Text';
import { theme } from './_vars';
import styles from './JournalArticle.module.scss';

export interface Author {
  firstName: string;
  lastName: string;
}

interface Tag {
  title: string;
  name: string;
  slug: string;
  url: string;
}

interface MediaDevice {
  url: string;
  title: string;
  type: string;
  contentType: string;
  width: number;
  height: number;
}

interface Media {
  showControls: boolean;
  muted: boolean;
  desktopCloudinary: string;
  mobileCloudinary: string;
  tabletCloudinary: string;
  desktop: MediaDevice;
  mobile: MediaDevice | null;
  tablet: MediaDevice | null;
}

interface ContentDescription {
  nodeType: string;
  data: any[];
  content: Array<{
    nodeType: string;
    data: any[];
    content?: Array<{
      nodeType: string;
      value: string;
      marks: any[];
      data: any[];
    }>;
    value?: string;
    marks?: any[];
  }>;
}

interface Block {
  type: string;
  title: string | null;
  subtitle: string | null;
  width: number;
  urlPosition: string;
  mediaTitle: string | null;
  mediaTitlePosition: string;
  textAlign: string;
  verticalAlign: string;
  link: string | null;
  linkTitle: string | null;
  isLinkWrapped: boolean;
  form: any | null;
  description: ContentDescription | string;
  media: Media | null;
}

interface Button {
  __typename: string;
  title: string;
  url: string;
  viewType: string;
}

interface ButtonGroupBlock {
  type: 'buttonGroup';
  buttons: Button[];
}

interface ArticleSectionProps {
  gridType: 'asSlider' | 'asDefault' | 'asGridOnMobile';
  rowAlign: 'center' | 'start' | 'end';
  maxWidth: number | null;
  verticalAlign: 'start' | 'end' | 'stretch';
  blockHeight: number | null;
  applyHeightTo: string;
  gap: number;
  paddingBetweenSection: number;
  blocks: Array<Block | ButtonGroupBlock>;
}

export interface Category {
  slug: string;
  title: string;
  url: string;
  backgroundColor?: string;
}

interface Thumbnail {
  showControls: boolean;
  muted: boolean;
  desktopCloudinary: string;
  mobileCloudinary: string;
  tabletCloudinary: string;
  desktop: MediaDevice;
  mobile: MediaDevice;
  tablet: MediaDevice;
}

interface RelatedArticle {
  objectID: string;
  id: string;
  type: string;
  title: string;
  slug: string;
  url: string;
  shortDescription: string;
  isPinnedWebsiteHome: boolean;
  isPinnedBlogHome: boolean;
  isPinnedCategory: boolean;
  publishDate: string;
  publishedAt: string;
  website: string;
  shareButtons: string[];
  author: Author | null;
  thumbnail: Thumbnail;
  categories: Category[];
}

interface NavigationProps {
  currentCategory: Category;
  categories: Category[];
  title: string;
  pageTitle: string;
  hideNavigation: boolean;
}

export interface JournalArticleProps {
  banner?: any | null;
  isSSR?: boolean;
  author?: Author;
  title?: string;
  publishedDate?: string;
  description?: string;
  relatedTags?: Tag[];
  articleSections?: ArticleSectionProps[];
  currentCategory?: Category;
  thumbnail?: any;
  relatedArticles?: RelatedArticle[];
  relatedProducts?: any[];
  navigationProps?: NavigationProps;
  shareButtons?: string[];
  productSliderTitle?: string;
  articlesSliderTitle?: string;
  relatedTagsTitle?: string;
  triggerRoute?: () => void;
}

const JournalArticle: React.FC<JournalArticleProps> = ({
  banner,
  isSSR,
  author,
  title,
  publishedDate,
  description,
  relatedTags,
  articleSections,
  currentCategory,
  thumbnail,
  relatedArticles,
  relatedProducts,
  navigationProps,
  shareButtons,
  productSliderTitle,
  articlesSliderTitle,
  relatedTagsTitle,
  triggerRoute,
}) => {
  const { globalNodeLoading } = useLayoutContext();

  return (
    <div className={styles.container}>
      <JournalNavigation {...navigationProps} customClasses={{ customContent: styles.navContent }} />
      <div
        className={clsx(styles.topBlock, {
          [`${styles.topBlockJag}`]: theme === 'jag',
          [styles.hasBanner]: !!banner,
        })}
      >
        <ArticleHeading
          author={author}
          title={title}
          publishedDate={publishedDate}
          description={description}
          lineColor={currentCategory?.backgroundColor}
          hideDescription
        />
        {banner && <JournalBanner isSSR={isSSR} banner={banner} />}
      </div>
      {articleSections &&
        articleSections.map((section, index) => (
          <ArticleSection
            sliderId={`unique-slider-${index}`}
            key={stableHash(JSON.stringify(section)) + index}
            isSSR={isSSR}
            gap={section.gap}
            gridType={section.gridType}
            rowAlign={section.rowAlign}
            verticalAlign={section.verticalAlign}
            paddingBetweenSection={section.paddingBetweenSection}
            articleItems={section.blocks}
            maxWidth={section.maxWidth}
            blockHeight={section.blockHeight}
            applyHeightTo={section.applyHeightTo}
          />
        ))}
      {shareButtons && shareButtons.length > 0 && (
        <div className={styles.socialBlock}>
          <Text as="h4" size="lg" align="center" className={styles.socialTitle}>
            share:
          </Text>
          <SocialShare
            product={{ name: title }}
            image={thumbnail}
            size={48}
            isInverted
            showShareIcon
            buttons={shareButtons}
            type="journal"
          />
        </div>
      )}
      {!!relatedProducts?.length && <JournalProductsSlider title={productSliderTitle} list={relatedProducts} />}

      {!!relatedArticles?.length && !globalNodeLoading && (
        <JournalArticlesSlider
          title={articlesSliderTitle}
          list={relatedArticles}
          currentCategory={currentCategory}
          triggerRoute={triggerRoute}
        />
      )}
      {!!relatedTags?.length && <TagList title={relatedTagsTitle} list={relatedTags} />}
    </div>
  );
};

export default JournalArticle;
